import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Minus, Plus, CreditCard, Loader2, Shield, AlertCircle, 
  Users, Tag, X, Check, Clock, MapPin, Calendar, Ticket, ChevronRight,
  ShieldCheck, Timer, LogIn, UserPlus, Sparkles
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { eventsApi, ticketsApi } from '../lib/api';
import { usePublicAuth } from '../context/PublicAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatEventDate, parseEventDate } from '../lib/dateUtils';
import BravxReferralInput from '../components/BravxReferralInput';
import VirtualQueueGate from '../components/queue/VirtualQueueGate';

// Timer Component
const CountdownTimer = ({ initialMinutes = 10, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState(initialMinutes * 60);
  
  useEffect(() => {
    if (timeLeft <= 0) {
      onExpire();
      return;
    }
    
    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft, onExpire]);
  
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const isUrgent = timeLeft <= 120; // Less than 2 minutes
  
  return (
    <motion.div 
      className={`flex items-center gap-2 px-4 py-2 rounded-full font-mono text-sm ${
        isUrgent 
          ? 'bg-red-500/20 border border-red-500/40 text-red-400' 
          : 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]'
      }`}
      animate={isUrgent ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 0.5, repeat: isUrgent ? Infinity : 0 }}
    >
      <Timer size={16} className={isUrgent ? 'animate-pulse' : ''} />
      <span className="font-bold">
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
      <span className="text-xs opacity-70">para completar</span>
    </motion.div>
  );
};

const CheckoutPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = usePublicAuth();
  const [event, setEvent] = useState(null);
  const [producer, setProducer] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [queueCleared, setQueueCleared] = useState(false);
  
  // Capture RRPP referral code from URL
  const rrppCode = searchParams.get('ref');
  // BRAVX referral preset (from share link `?bravx=username`)
  const bravxPreset = searchParams.get('bravx');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [ticketDnis, setTicketDnis] = useState(['']);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [bravxRef, setBravxRef] = useState(bravxPreset || '');
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  // Require authentication to purchase tickets
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Build return URL with current checkout parameters
      const currentUrl = `/checkout/${eventId}${window.location.search}`;
      navigate(`/iniciar-sesion?redirect=${encodeURIComponent(currentUrl)}&msg=login_required`);
    }
  }, [authLoading, isAuthenticated, eventId, navigate]);

  // Pre-fill form with user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.full_name || prev.name,
        email: user.email || prev.email
      }));
      if (user.dni) {
        setTicketDnis(prev => {
          const newDnis = [...prev];
          newDnis[0] = user.dni;
          return newDnis;
        });
      }
    }
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use full endpoint to get producer info too
        const fullRes = await eventsApi.getFullDetails(eventId).catch(() => null);
        
        if (fullRes?.data) {
          setEvent(fullRes.data.event);
          setProducer(fullRes.data.producer);
          const activeTiers = fullRes.data.tiers?.filter(t => t.is_active) || [];
          setTiers(activeTiers);
          
          const tierIdFromUrl = searchParams.get('tier');
          if (tierIdFromUrl && activeTiers.length > 0) {
            const tier = activeTiers.find(t => t.id === tierIdFromUrl);
            if (tier) setSelectedTier(tier);
          } else if (activeTiers.length === 1) {
            setSelectedTier(activeTiers[0]);
          }
        } else {
          // Fallback to old endpoints
          const [eventRes, tiersRes] = await Promise.all([
            eventsApi.getById(eventId),
            eventsApi.getTicketTiersPublic(eventId).catch(() => ({ data: [] }))
          ]);
          setEvent(eventRes.data);
          const activeTiers = tiersRes.data?.filter(t => t.is_active) || [];
          setTiers(activeTiers);
          
          const tierIdFromUrl = searchParams.get('tier');
          if (tierIdFromUrl && activeTiers.length > 0) {
            const tier = activeTiers.find(t => t.id === tierIdFromUrl);
            if (tier) setSelectedTier(tier);
          } else if (activeTiers.length === 1) {
            setSelectedTier(activeTiers[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Error al cargar el evento');
        navigate('/eventos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId, navigate, searchParams]);

  const handleSessionExpire = useCallback(() => {
    setSessionExpired(true);
    toast.error('Tu sesión de compra ha expirado');
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDniChange = (index, value) => {
    const numericValue = value.replace(/\D/g, '');
    setTicketDnis(prev => {
      const newDnis = [...prev];
      newDnis[index] = numericValue;
      return newDnis;
    });
  };

  const getMaxTicketsPerOrder = () => {
    const eventMax = event?.max_tickets_per_order || 10;
    // Per-tier limit set by the producer (if any) takes precedence
    const tierMax = selectedTier?.max_per_order;
    const available = selectedTier
      ? (selectedTier.quantity - (selectedTier.sold || 0))
      : (event?.ticket_quantity || 0) - (event?.tickets_sold || 0);
    // Hard cap of 10 to keep the UI sane; respect ALL configured limits.
    const limits = [eventMax, available, 10];
    if (tierMax && tierMax > 0) limits.push(tierMax);
    return Math.max(1, Math.min(...limits));
  };

  // "Pay X, get Y" promo — the paid quantity grants bonus free tickets
  const promoActive = (() => {
    const t = selectedTier || {};
    const px = Number(t.promo_pay_x || 0);
    const gy = Number(t.promo_get_y || 0);
    if (px < 1 || gy <= px) return false;
    if (t.promo_valid_until) {
      const vu = new Date(t.promo_valid_until);
      if (!isNaN(vu.getTime()) && vu <= new Date()) return false;
    }
    if (t.promo_max_redemptions && t.promo_max_redemptions > 0) {
      const used = Number(t.promo_redemptions_count || 0);
      if (used >= t.promo_max_redemptions) return false;
    }
    return true;
  })();
  const promoPayX = Number(selectedTier?.promo_pay_x || 0);
  const promoGetY = Number(selectedTier?.promo_get_y || 0);
  const promoBonusFor = (q) => (promoActive ? Math.floor(q / promoPayX) * (promoGetY - promoPayX) : 0);
  const promoBonusUnits = promoBonusFor(quantity);
  const deliveredQty = quantity + promoBonusUnits;

  // One DNI field per DELIVERED ticket (paid + promo bonus tickets)
  useEffect(() => {
    setTicketDnis(prev => {
      if (deliveredQty > prev.length) return [...prev, ...Array(deliveredQty - prev.length).fill('')];
      if (deliveredQty < prev.length) return prev.slice(0, deliveredQty);
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveredQty]);

  // Clamp quantity to the per-tier max whenever the tier or its availability changes
  useEffect(() => {
    if (!selectedTier) return;
    const max = getMaxTicketsPerOrder();
    if (quantity > max) {
      setQuantity(max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTier?.id, selectedTier?.max_per_order, selectedTier?.quantity, selectedTier?.sold]);

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    const maxAllowed = getMaxTicketsPerOrder();
    
    if (newQuantity >= 1 && newQuantity <= maxAllowed) {
      const available = selectedTier
        ? (selectedTier.quantity - (selectedTier.sold || 0))
        : (event?.ticket_quantity || 0) - (event?.tickets_sold || 0);
      if (delta > 0 && newQuantity + promoBonusFor(newQuantity) > available) {
        toast.error('No hay stock suficiente para esa cantidad (la promo incluye entradas de regalo)');
        return;
      }
      setQuantity(newQuantity);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (sessionExpired) {
      toast.error('Tu sesión expiró. Por favor, volvé a intentar.');
      navigate(`/evento/${eventId}`);
      return;
    }
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    const invalidDnis = ticketDnis.filter((dni) => !dni || dni.length < 7);
    if (invalidDnis.length > 0) {
      toast.error(`Por favor ingresá un DNI válido para ${ticketDnis.length === 1 ? 'tu entrada' : 'cada entrada'}`);
      return;
    }

    const uniqueDnis = new Set(ticketDnis);
    if (uniqueDnis.size !== ticketDnis.length) {
      toast.error('Cada entrada debe tener un DNI diferente');
      return;
    }

    setSubmitting(true);
    try {
      const response = await ticketsApi.purchase({
        event_id: eventId,
        quantity,
        buyer_name: formData.name,
        buyer_email: formData.email,
        buyer_phone: formData.phone,
        buyer_dni: ticketDnis[0],
        holder_dnis: ticketDnis,
        tier_id: selectedTier?.id || null,
        user_id: user?.user_id || user?.id || null,
        discount_code: appliedDiscount?.code || null,
        rrpp_code: rrppCode || null,
        bravx_referral_username: (bravxRef || '').trim().toLowerCase() || null
      });

      const order = response.data;

      if (order.init_point) {
        window.location.href = order.init_point;
      } else {
        toast.success('¡Orden creada! Recibirás un email con los detalles.');
        navigate(`/checkout/success?order_id=${order.id}`);
      }
    } catch (error) {
      console.error('Error purchasing tickets:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    
    setApplyingDiscount(true);
    try {
      const response = await eventsApi.validateDiscountCode(eventId, discountCode.trim().toUpperCase());
      setAppliedDiscount(response);
      toast.success('¡Código aplicado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido o expirado');
      setAppliedDiscount(null);
    } finally {
      setApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          className="w-10 h-10 rounded-full border-2 border-white/20 border-t-[#D4AF37]"
        />
      </div>
    );
  }

  // Virtual queue gate — backend instantly returns "ready" if queue_enabled=false
  if (event && !queueCleared) {
    return (
      <VirtualQueueGate
        eventId={eventId}
        eventTitle={event.title}
        eventImage={event.image_url}
        onReady={() => setQueueCleared(true)}
      />
    );
  }

  // Session Expired Screen
  if (sessionExpired) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <Navbar />
        <div className="pt-32 pb-24 px-4 flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <Timer size={32} className="text-red-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Sesión expirada</h1>
            <p className="text-white/50 mb-8">
              Tu tiempo para completar la compra ha terminado. Por favor, volvé a intentar.
            </p>
            <Link 
              to={`/evento/${eventId}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#D4AF37]/90 transition-all"
            >
              <ArrowLeft size={16} /> Volver al evento
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Calculate pricing
  const isSubscriber = user?.subscription_active;
  const unitPrice = selectedTier ? selectedTier.price : event?.ticket_price || 0;
  
  const calculateServiceFee = () => {
    if (selectedTier?.free_service_for_subscribers && isSubscriber) return 0;
    const feeValue = event?.service_fee || 0;
    // Use Math.ceil to ensure even small fees are charged (minimum $1 if fee > 0)
    const calculatedFee = Math.ceil(unitPrice * (feeValue / 100));
    return feeValue > 0 && unitPrice > 0 ? Math.max(calculatedFee, 1) : 0;
  };
  
  const serviceFee = calculateServiceFee();
  
  // Calculate discount - check if tier-specific coupon matches selected tier
  let discountAmount = 0;
  let discountError = null;
  if (appliedDiscount) {
    // If coupon is tier-specific, check if selected tier matches
    if (appliedDiscount.tier_id && selectedTier?.id !== appliedDiscount.tier_id) {
      discountError = `Este código solo aplica a: ${appliedDiscount.tier_name || 'otra entrada'}`;
      discountAmount = 0;
    } else {
      const subtotal = unitPrice * quantity;
      if (appliedDiscount.discount_type === 'percentage') {
        discountAmount = (subtotal * appliedDiscount.discount_value) / 100;
      } else {
        discountAmount = appliedDiscount.discount_value * quantity;
      }
    }
  }
  
  const subtotal = unitPrice * quantity;
  const serviceFeeTotal = serviceFee * quantity;

  // Promo savings = value of the free tickets the buyer receives
  const promoSavings = promoBonusUnits * unitPrice;

  const total = Math.max(0, subtotal + serviceFeeTotal - discountAmount);
  const primaryColor = producer?.branding?.primary_color || '#D4AF37';
  const eventDate = event?.date ? parseEventDate(event.date) : new Date();

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Navbar />
      
      {/* Timer Bar - Fixed at top (no navbar on mobile, so top-0) */}
      <div className="fixed top-0 md:top-24 left-0 right-0 z-40 bg-[#030303] md:bg-[#030303]/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-3 md:py-2.5 flex items-center justify-between safe-area-top">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Volver</span>
          </button>
          
          <CountdownTimer initialMinutes={10} onExpire={handleSessionExpire} />
        </div>
      </div>

      <section className="pt-16 md:pt-44 pb-32 md:pb-24 px-4" data-testid="checkout-page">
        <div className="max-w-5xl mx-auto">
          {/* Event Header - Compact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-start gap-4 p-4 bg-white/[0.02] rounded-2xl border border-white/10">
              {/* Event Image */}
              <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 rounded-xl overflow-hidden">
                <img
                  src={event?.image_url || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400'}
                  alt={event?.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Event Info */}
              <div className="flex-1 min-w-0">
                {/* Producer */}
                {producer && (
                  <Link 
                    to={`/productora/${producer.slug}`}
                    className="inline-flex items-center gap-1.5 text-white/50 text-xs mb-1 hover:text-white/70 transition-colors"
                  >
                    {producer.logo ? (
                      <img src={producer.logo} alt={producer.name} className="w-4 h-4 rounded-full" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[8px] text-[#D4AF37] font-bold">
                        {producer.name?.charAt(0)}
                      </div>
                    )}
                    <span>{producer.name}</span>
                    {producer.is_verified && <ShieldCheck size={10} className="text-emerald-400" />}
                  </Link>
                )}
                
                <h2 className="font-bold text-lg uppercase tracking-tight truncate">{event?.title}</h2>
                
                <div className="flex flex-wrap items-center gap-3 mt-1 text-white/40 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} style={{ color: primaryColor }} />
                    {format(eventDate, "d MMM, HH:mm", { locale: es })}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin size={12} style={{ color: primaryColor }} />
                    <span className="truncate max-w-[150px]">{event?.location}</span>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            
            {/* Left: Form - 3 columns */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-3 order-last lg:order-first"
            >
              <div className="bg-white/[0.02] rounded-2xl border border-white/10 overflow-hidden">
                <div className="p-5 border-b border-white/10">
                  <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <Users size={16} style={{ color: primaryColor }} />
                    Datos del comprador
                  </h3>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-5">
                  {/* DNI Banner */}
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="text-emerald-400 flex-shrink-0" size={18} />
                      <div>
                        <p className="text-emerald-400 font-semibold text-sm">
                          {ticketDnis.length === 1 ? 'Tu DNI será tu entrada' : 'Cada entrada requiere un DNI'}
                        </p>
                        <p className="text-emerald-500/70 text-xs mt-1">
                          Presentá tu DNI físico o el QR que recibirás por email
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Promo banner: buyer receives bonus tickets */}
                  {promoBonusUnits > 0 && (
                    <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4" data-testid="checkout-promo-banner">
                      <p className="text-[#D4AF37] font-bold text-sm">
                        🎁 Promo {selectedTier?.promo_get_y}x{selectedTier?.promo_pay_x}: recibís {deliveredQty} entradas al precio de {quantity}
                      </p>
                      <p className="text-[#D4AF37]/70 text-xs mt-1">
                        Ahorrás ${promoSavings.toLocaleString()} · Ingresá un DNI por cada entrada (incluidas las de regalo)
                      </p>
                    </div>
                  )}

                  {/* DNI Inputs */}
                  <div className="space-y-3">
                    {ticketDnis.map((dni, index) => (
                      <div key={index}>
                        <Label className="text-xs uppercase tracking-wider text-white/40 mb-2 flex items-center gap-2">
                          <Ticket size={12} style={{ color: primaryColor }} />
                          {ticketDnis.length === 1 ? 'DNI (Entrada)' : `Entrada ${index + 1} - DNI${index >= quantity ? ' · GRATIS 🎁' : ''}`}
                        </Label>
                        <Input
                          type="text"
                          value={dni}
                          onChange={(e) => handleDniChange(index, e.target.value)}
                          placeholder="12345678"
                          required
                          maxLength={8}
                          className="bg-white/5 border-white/10 focus:border-[#D4AF37] text-lg font-mono tracking-widest"
                          data-testid={`checkout-dni-${index}`}
                        />
                        {index === 0 && (
                          <p className="text-[10px] text-white/30 mt-1">Sin puntos ni guiones</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Contact Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">
                        Nombre completo
                      </Label>
                      <Input
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Tu nombre"
                        required
                        className="bg-white/5 border-white/10 focus:border-[#D4AF37]"
                        data-testid="checkout-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">
                        Teléfono
                      </Label>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+54 11 1234-5678"
                        required
                        className="bg-white/5 border-white/10 focus:border-[#D4AF37]"
                        data-testid="checkout-phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">
                      Email
                    </Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="tu@email.com"
                      required
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37]"
                      data-testid="checkout-email"
                    />
                    <p className="text-[10px] text-white/30 mt-1">Recibirás tus entradas aquí</p>
                  </div>

                  {/* BRAVX referral */}
                  <BravxReferralInput
                    value={bravxRef}
                    onChange={setBravxRef}
                    totalAmount={total}
                    currentUserId={user?.user_id || user?.id || null}
                    disabled={submitting}
                  />

                  {/* Submit - Mobile only */}
                  <div className="lg:hidden pt-4">
                    <Button
                      type="submit"
                      disabled={submitting || sessionExpired}
                      className="w-full py-6 text-base font-bold uppercase tracking-wider rounded-xl"
                      style={{ background: primaryColor, color: '#000' }}
                      data-testid="checkout-submit-mobile"
                    >
                      {submitting ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        `Pagar $${total.toLocaleString()}`
                      )}
                    </Button>
                    {event?.custom_terms && (
                      <p className="text-center text-[11px] text-white/50 mt-3 leading-relaxed" data-testid="checkout-terms-notice">
                        Al pagar aceptás los <button
                          type="button"
                          onClick={() => document.getElementById('event-terms-modal')?.showModal?.()}
                          className="text-white/80 underline underline-offset-2 hover:text-white"
                        >términos específicos de este evento</button> y la <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-white/80 underline underline-offset-2 hover:text-white">política general</a>.
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </motion.div>

            {/* Right: Summary - 2 columns */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2 order-first lg:order-last"
            >
              <div className="lg:sticky lg:top-32 space-y-4">
                {/* Ticket Selection */}
                <div className="bg-white/[0.02] rounded-2xl border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                      <Ticket size={16} style={{ color: primaryColor }} />
                      Tu pedido
                    </h3>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Selected Tier */}
                    {selectedTier && (
                      <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-bold text-sm">{selectedTier.name}</span>
                            {selectedTier.free_service_for_subscribers && (
                              <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold bg-[#D4AF37]/20 text-[#D4AF37] rounded">
                                BRAVX
                              </span>
                            )}
                          </div>
                          <span className="font-mono font-bold">${unitPrice.toLocaleString()}</span>
                        </div>
                      </div>
                    )}

                    {/* Quantity */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-white/50 text-sm">Cantidad</span>
                        {selectedTier?.max_per_order > 0 && quantity >= selectedTier.max_per_order && (
                          <span className="text-[10px] text-amber-300 mt-0.5" data-testid="tier-max-per-order-hint">
                            Máx. {selectedTier.max_per_order} por orden en esta tanda
                          </span>
                        )}
                      </div>
                      <div className="flex items-center border border-white/10 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(-1)}
                          disabled={quantity <= 1}
                          className="p-2 hover:bg-white/5 disabled:opacity-30 transition-colors"
                          data-testid="quantity-minus"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-4 font-mono font-bold" data-testid="quantity-display">
                          {quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(1)}
                          disabled={quantity >= getMaxTicketsPerOrder()}
                          className="p-2 hover:bg-white/5 disabled:opacity-30 transition-colors"
                          data-testid="quantity-plus"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Discount Code */}
                    <div>
                      {appliedDiscount ? (
                        <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                          <div className="flex items-center gap-2">
                            <Check size={16} className="text-emerald-400" />
                            <span className="text-emerald-400 font-mono text-sm">{appliedDiscount.code}</span>
                          </div>
                          <button onClick={removeDiscount} className="text-emerald-400/60 hover:text-emerald-400">
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={14} />
                            <input
                              value={discountCode}
                              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                              placeholder="Código"
                              className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-sm font-mono uppercase outline-none focus:border-[#D4AF37]"
                              data-testid="discount-code-input"
                            />
                          </div>
                          <Button
                            type="button"
                            onClick={handleApplyDiscount}
                            disabled={!discountCode.trim() || applyingDiscount}
                            size="sm"
                            className="bg-white/10 hover:bg-white/20"
                          >
                            {applyingDiscount ? <Loader2 className="animate-spin" size={14} /> : 'OK'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing Breakdown */}
                  <div className="p-4 border-t border-white/10 space-y-2 text-sm">
                    <div className="flex justify-between text-white/50">
                      <span>Subtotal ({quantity}x)</span>
                      <span className="font-mono">${subtotal.toLocaleString()}</span>
                    </div>
                    {serviceFee > 0 && (
                      <div className="flex justify-between text-white/50">
                        <span>Servicio</span>
                        <span className="font-mono">${serviceFeeTotal.toLocaleString()}</span>
                      </div>
                    )}
                    {selectedTier?.free_service_for_subscribers && isSubscriber && event?.service_fee > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          Ahorro BRAVX
                        </span>
                        <span className="font-mono">-${(Math.round(unitPrice * (event.service_fee / 100)) * quantity).toLocaleString()}</span>
                      </div>
                    )}
                    {promoActive && promoBonusUnits > 0 && (
                      <div className="flex justify-between text-[#D4AF37]" data-testid="checkout-promo-line">
                        <span className="flex items-center gap-1 font-bold">
                          <Sparkles size={12} />
                          Promo {selectedTier.promo_get_y}x{selectedTier.promo_pay_x} · +{promoBonusUnits} de regalo
                        </span>
                        <span className="font-mono">Ahorrás ${promoSavings.toLocaleString()}</span>
                      </div>
                    )}
                    {appliedDiscount && discountAmount > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span>Descuento ({appliedDiscount.discount_value}%)</span>
                        <span className="font-mono">-${discountAmount.toLocaleString()}</span>
                      </div>
                    )}
                    {appliedDiscount && discountError && (
                      <div className="flex justify-between text-yellow-400 text-sm">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {discountError}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  <div className="p-4 border-t border-white/10 flex justify-between items-center">
                    <span className="font-bold uppercase">Total</span>
                    <span className="text-2xl font-black font-mono" data-testid="checkout-total">
                      ${total.toLocaleString()}
                    </span>
                  </div>

                  {/* Submit - Desktop */}
                  <div className="p-4 hidden lg:block">
                    <Button
                      type="submit"
                      form="checkout-form"
                      onClick={handleSubmit}
                      disabled={submitting || sessionExpired}
                      className="w-full py-5 text-base font-bold uppercase tracking-wider rounded-xl"
                      style={{ background: primaryColor, color: '#000' }}
                      data-testid="checkout-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CreditCard size={18} className="mr-2" />
                          Pagar ${total.toLocaleString()}
                        </>
                      )}
                    </Button>
                    
                    <p className="text-center text-[10px] text-white/30 mt-3 flex items-center justify-center gap-1">
                      <Shield size={10} />
                      Pago seguro con Mercado Pago
                    </p>
                    {event?.custom_terms && (
                      <p className="text-center text-[10px] text-white/40 mt-2 leading-relaxed px-2" data-testid="checkout-terms-notice-mobile">
                        Al pagar aceptás los <button
                          type="button"
                          onClick={() => document.getElementById('event-terms-modal')?.showModal?.()}
                          className="text-white/70 underline underline-offset-2 hover:text-white"
                        >términos específicos de este evento</button>.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Event Terms Modal — shown when user clicks the inline link */}
      {event?.custom_terms && (
        <dialog
          id="event-terms-modal"
          className="bg-black/95 backdrop-blur-md border border-white/10 rounded-2xl max-w-xl w-[92vw] p-0 text-white"
          onClick={(e) => { if (e.target.tagName === 'DIALOG') e.target.close(); }}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Shield size={18} style={{ color: primaryColor }} />
                Términos de este evento
              </h3>
              <button
                onClick={() => document.getElementById('event-terms-modal')?.close?.()}
                className="text-white/40 hover:text-white transition-colors"
                aria-label="Cerrar"
              >✕</button>
            </div>
            <div className="text-sm text-white/75 leading-relaxed whitespace-pre-line max-h-[60vh] overflow-y-auto">
              {event.custom_terms}
            </div>
            <button
              onClick={() => document.getElementById('event-terms-modal')?.close?.()}
              className="mt-5 w-full px-4 py-2.5 rounded-lg bg-white text-black font-semibold text-sm hover:bg-white/90 transition"
            >
              Entendido
            </button>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default CheckoutPage;
